import React, { useMemo } from 'react';
import {
  ReportField,
  ReportFlowPages,
  ReportFooter,
  ReportHeader,
  ReportMeta,
  ReportMetaRow,
  ReportNote,
  ReportTitleRow,
} from '../../../components/ui/Report';
import { formatDate as formatDateGlobal } from '../../../config/appConfig';

const WEEKDAY_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export type WeeklySummaryDayCell = {
  workDate: string;
  workHours: number;
  restHours: number;
  compliant: boolean;
  violationCodes: string[];
};

export type WeeklySummaryCrew = {
  crewId: number;
  crewName: string;
  rankName?: string | null;
  totalWork: number;
  totalRest: number;
  anyNc: boolean;
  days: WeeklySummaryDayCell[];
};

export type WeeklySummaryReportData = {
  vesselId: number;
  vesselName?: string | null;
  vesselAbrv?: string | null;
  weekStart: string;
  weekEnd: string;
  crew: WeeklySummaryCrew[];
  ruleSet?: { id?: number; name?: string } | null;
  managementCompany?: {
    id: number;
    name: string;
    logoStorageKey?: string | null;
  } | null;
};

function fmtHours(n: number): string {
  if (!Number.isFinite(n)) return '—';
  return n % 1 === 0 ? String(n) : n.toFixed(1);
}

function addDaysIso(iso: string, days: number) {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

type WeeklySummaryReportProps = {
  data: WeeklySummaryReportData;
  companyLogoSrc?: string | null;
};

function TableHead({ weekDates }: { weekDates: string[] }) {
  return (
    <table className="wrh-weekly-print-table wrh-weekly-print-table-head">
      <thead>
        <tr>
          <th className="wrh-week-col-crew">Crew</th>
          <th className="wrh-week-col-rank">Rank</th>
          {weekDates.map((date, i) => (
            <th key={date} className="wrh-week-col-day day">
              <div>{WEEKDAY_SHORT[i]}</div>
              <div className="day-num">{date.slice(8)}</div>
            </th>
          ))}
          <th className="wrh-week-col-num">Work</th>
          <th className="wrh-week-col-num">Rest</th>
          <th className="wrh-week-col-status">Status</th>
        </tr>
      </thead>
    </table>
  );
}

function LineRow({ crew, weekDates }: { crew: WeeklySummaryCrew; weekDates: string[] }) {
  const byDate = new Map(crew.days.map((d) => [d.workDate.slice(0, 10), d]));
  return (
    <table
      className={`wrh-weekly-print-table wrh-weekly-print-row-table${crew.anyNc ? ' nc' : ''}`}
    >
      <tbody>
        <tr className={crew.anyNc ? 'nc' : undefined}>
          <td className="wrh-week-col-crew crew">{crew.crewName}</td>
          <td className="wrh-week-col-rank rank">{crew.rankName || '—'}</td>
          {weekDates.map((date) => {
            const d = byDate.get(date);
            if (!d) {
              return (
                <td key={date} className="wrh-week-col-day day muted">
                  —
                </td>
              );
            }
            return (
              <td
                key={date}
                className={`wrh-week-col-day ${d.compliant ? 'day ok' : 'day nc'}`}
                title={
                  d.compliant ? undefined : `NC: ${(d.violationCodes ?? []).join(', ') || 'issue'}`
                }
              >
                {fmtHours(d.workHours)}
              </td>
            );
          })}
          <td className="wrh-week-col-num num">{fmtHours(crew.totalWork)}</td>
          <td className="wrh-week-col-num num">{fmtHours(crew.totalRest)}</td>
          <td className="wrh-week-col-status status">
            <span className={crew.anyNc ? 'wrh-weekly-print-badge nc' : 'wrh-weekly-print-badge ok'}>
              {crew.anyNc ? 'NC' : 'OK'}
            </span>
          </td>
        </tr>
      </tbody>
    </table>
  );
}

export const WeeklySummaryReport: React.FC<WeeklySummaryReportProps> = ({
  data,
  companyLogoSrc = null,
}) => {
  const crew = data.crew ?? [];
  const weekDates = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDaysIso(data.weekStart, i)),
    [data.weekStart],
  );

  const ncCrew = crew.filter((c) => c.anyNc).length;
  const okCrew = crew.length - ncCrew;
  const totalWork = crew.reduce((s, c) => s + (c.totalWork || 0), 0);
  const totalRest = crew.reduce((s, c) => s + (c.totalRest || 0), 0);
  const vesselLabel = `${data.vesselName || '—'}${data.vesselAbrv ? ` (${data.vesselAbrv})` : ''}`;
  const weekLabel = `${formatDateGlobal(data.weekStart)} – ${formatDateGlobal(data.weekEnd)}`;

  const firstHeader = useMemo(
    () => (
      <div className="wrh-weekly-print-flow-header">
        <ReportHeader>
          <ReportTitleRow
            title="Working / rest hours — weekly summary"
            formId="MLC / STCW"
            logoSrc={companyLogoSrc}
            logoAlt={data.managementCompany?.name || 'Management company logo'}
          />
          <ReportMeta>
            <ReportMetaRow>
              <ReportField label="Vessel" value={vesselLabel} />
              <ReportField label="Week" value={weekLabel} />
              <ReportField label="Rule set" value={data.ruleSet?.name || '—'} />
            </ReportMetaRow>
            <ReportMetaRow>
              <ReportField label="Company" value={data.managementCompany?.name || '—'} />
              <ReportField label="Crew" value={String(crew.length)} />
              <ReportField label="OK / NC" value={`${okCrew} / ${ncCrew}`} />
            </ReportMetaRow>
            <ReportMetaRow>
              <ReportField label="Total work" value={`${fmtHours(totalWork)}h`} />
              <ReportField label="Total rest" value={`${fmtHours(totalRest)}h`} />
            </ReportMetaRow>
          </ReportMeta>
          <p className="ui-report-legal">
            Day cells show work hours for that calendar day. Red cells are non-conformities against the
            active WRH rule set (sliding windows).
          </p>
        </ReportHeader>
        <TableHead weekDates={weekDates} />
      </div>
    ),
    [
      companyLogoSrc,
      crew.length,
      data.managementCompany?.name,
      data.ruleSet?.name,
      ncCrew,
      okCrew,
      totalRest,
      totalWork,
      vesselLabel,
      weekDates,
      weekLabel,
    ],
  );

  const continueHeader = useMemo(
    () => (
      <div className="wrh-weekly-print-flow-header">
        <div className="wrh-weekly-print-continue-bar">
          <span className="wrh-weekly-print-continue-title">WRH weekly summary</span>
          <span className="wrh-weekly-print-continue-vessel">{vesselLabel}</span>
          <span className="wrh-weekly-print-continue-week">{weekLabel}</span>
          <span className="wrh-weekly-print-continue-hint">continued</span>
        </div>
        <TableHead weekDates={weekDates} />
      </div>
    ),
    [vesselLabel, weekDates, weekLabel],
  );

  const lastFooter = useMemo(
    () => (
      <ReportFooter>
        <ReportNote>
          Print date: {new Date().toLocaleString('en-GB')}. Day values are calendar-day work hours;
          compliance uses sliding 24h / 7d windows.
        </ReportNote>
      </ReportFooter>
    ),
    [],
  );

  const { items, itemKeys } = useMemo(() => {
    if (crew.length === 0) {
      return {
        items: [
          <p key="empty" className="wrh-weekly-print-empty">
            No WRH days recorded for this week.
          </p>,
        ],
        itemKeys: ['empty'],
      };
    }
    return {
      items: crew.map((row) => (
        <LineRow key={row.crewId} crew={row} weekDates={weekDates} />
      )),
      itemKeys: crew.map((row) => row.crewId),
    };
  }, [crew, weekDates]);

  return (
    <ReportFlowPages
      className="wrh-weekly-print"
      size="landscape"
      firstHeader={firstHeader}
      continueHeader={continueHeader}
      lastFooter={lastFooter}
      items={items}
      itemKeys={itemKeys}
    />
  );
};

export default WeeklySummaryReport;
